import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";
import { darkTheme, lightTheme } from "./colors";
import { fontFamilies, typography } from "./typography";
import { accentsDark, accentsLight, type AccentTokens } from "./accents";
import { shadows } from "./shadows";
import { radii } from "./radii";
import { gradients } from "./gradients";

// Flatten accent palette into CSS variable name -> value pairs.
// Produces e.g. { "--accent-healthy-wash": "#E8E8DF", ... }
const flattenAccents = (palette: AccentTokens): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const role of Object.keys(palette) as Array<keyof AccentTokens>) {
    const tone = palette[role];
    out[`--accent-${role}-wash`] = tone.wash;
    out[`--accent-${role}-rule`] = tone.rule;
    out[`--accent-${role}-fg`] = tone.fg;
  }
  return out;
};

const config = defineConfig({
  ...defaultConfig,
  cssVarsRoot: ":where(html)",
  globalCss: {
    ":root, body": {
      // Font CSS variables
      "--font-body": fontFamilies.body,
      "--font-heading": fontFamilies.heading,
      "--font-mono": fontFamilies.mono,
      // Light theme CSS variables
      "--bg-hover": lightTheme.bg.hover,
      "--text-primary": lightTheme.text.primary,
      "--text-secondary": lightTheme.text.secondary,
      "--border-default": lightTheme.border.default,
      "--modal-header-text": lightTheme.modal.headerText,
      "--modal-close-icon": lightTheme.modal.closeIconColor,
      "--modal-close-icon-hover": lightTheme.modal.closeIconHover,
      "--alert-error-bg": lightTheme.alert.error.bg,
      "--alert-error-text": lightTheme.alert.error.text,
      "--alert-success-bg": lightTheme.alert.success.bg,
      "--alert-success-text": lightTheme.alert.success.text,
      "--table-header-bg": lightTheme.table.headerBg,
      "--table-header-text": lightTheme.table.headerText,
      "--table-row-bg": lightTheme.table.rowBg,
      "--table-row-hover": lightTheme.table.rowHover,
      "--table-border": lightTheme.table.border,
      // Override Chakra's card colors
      "--chakra-colors-bg-panel": lightTheme.card.bg,
      // Override Chakra's blue.solid semantic token (default maps to blue.600 = #2563eb)
      // Brand Electric Blue is blue.500 = #3B82F6. This fixes all colorPalette="blue" components:
      // Button (solid), Badge (solid), Checkbox, Switch, Tag, etc.
      "--chakra-colors-blue-solid": "#3B82F6",
      // Textarea colors
      "--textarea-bg": lightTheme.textarea.bg,
      "--textarea-border": lightTheme.textarea.border,
      "--textarea-border-focus": lightTheme.textarea.borderFocus,
      "--textarea-text": lightTheme.textarea.text,
      "--textarea-placeholder": lightTheme.textarea.placeholder,
      // Code block colors
      "--code-block-bg": lightTheme.codeBlock.bg,
      "--code-block-text": lightTheme.codeBlock.text,
      "--code-block-border": lightTheme.codeBlock.border,
      // Line highlights in a before/after diff (suggestion previews). Washes rather than solid fills,
      // so the monospace text keeps its own colour and stays readable on either side.
      "--diff-added-bg": "rgba(22, 163, 74, 0.12)",
      "--diff-removed-bg": "rgba(220, 38, 38, 0.10)",
      // Common border colors
      "--border-subtle": lightTheme.border.subtle,
      "--card-border": lightTheme.card.border,
      // Text colors
      "--text-secondary-color": lightTheme.text.secondary,
      // Button colors
      "--button-bg": lightTheme.bg.secondary,
      "--button-hover-bg": lightTheme.bg.hover,
      // Tooltip
      "--tooltip-bg": lightTheme.tooltip.bg,
      "--tooltip-fg": lightTheme.tooltip.fg,
      "--tooltip-border": lightTheme.tooltip.border,
      // Skeleton
      "--skeleton-bg": lightTheme.skeleton.bg,
      "--skeleton-highlight": lightTheme.skeleton.highlight,
      // Soft-editorial shadows
      "--shadow-card": shadows.light.card,
      "--shadow-card-hover": shadows.light.cardHover,
      "--shadow-popover": shadows.light.popover,
      // Radii (px)
      "--radius-input": `${radii.input}px`,
      "--radius-tile": `${radii.tile}px`,
      "--radius-card": `${radii.card}px`,
      "--radius-toast": `${radii.toast}px`,
      "--radius-modal": `${radii.modal}px`,
      "--radius-pill": `${radii.pill}px`,
      // Accents (light)
      ...flattenAccents(accentsLight),
      // Brand gradients (identical in light + dark — they are themselves dark)
      "--gradient-hero-blue": gradients.heroBlue,
      "--gradient-hero-grey": gradients.heroGrey,
    },
    "html.dark, html.dark body": {
      colorScheme: "dark",
      // Dark theme CSS variables
      "--bg-hover": darkTheme.bg.hover,
      "--text-primary": darkTheme.text.primary,
      "--text-secondary": darkTheme.text.secondary,
      "--border-default": darkTheme.border.default,
      "--modal-header-text": darkTheme.modal.headerText,
      "--modal-close-icon": darkTheme.modal.closeIconColor,
      "--modal-close-icon-hover": darkTheme.modal.closeIconHover,
      "--alert-error-bg": darkTheme.alert.error.bg,
      "--alert-error-text": darkTheme.alert.error.text,
      "--alert-success-bg": darkTheme.alert.success.bg,
      "--alert-success-text": darkTheme.alert.success.text,
      "--table-header-bg": darkTheme.table.headerBg,
      "--table-header-text": darkTheme.table.headerText,
      "--table-row-bg": darkTheme.table.rowBg,
      "--table-row-hover": darkTheme.table.rowHover,
      "--table-border": darkTheme.table.border,
      // Override Chakra's card colors to match navbar
      "--chakra-colors-bg-panel": "#28282B !important",
      // Keep brand Electric Blue for dark mode too
      "--chakra-colors-blue-solid": "#3B82F6",
      // Textarea colors
      "--textarea-bg": darkTheme.textarea.bg,
      "--textarea-border": darkTheme.textarea.border,
      "--textarea-border-focus": darkTheme.textarea.borderFocus,
      "--textarea-text": darkTheme.textarea.text,
      "--textarea-placeholder": darkTheme.textarea.placeholder,
      // Code block colors
      "--code-block-bg": darkTheme.codeBlock.bg,
      "--code-block-text": darkTheme.codeBlock.text,
      "--code-block-border": darkTheme.codeBlock.border,
      "--diff-added-bg": "rgba(34, 197, 94, 0.18)",
      "--diff-removed-bg": "rgba(239, 68, 68, 0.16)",
      // Common border colors
      "--border-subtle": darkTheme.border.subtle,
      "--card-border": darkTheme.card.border,
      // Text colors
      "--text-secondary-color": darkTheme.text.secondary,
      // Button colors
      "--button-bg": darkTheme.bg.tertiary,
      "--button-hover-bg": darkTheme.bg.hover,
      // Tooltip
      "--tooltip-bg": darkTheme.tooltip.bg,
      "--tooltip-fg": darkTheme.tooltip.fg,
      "--tooltip-border": darkTheme.tooltip.border,
      // Skeleton
      "--skeleton-bg": darkTheme.skeleton.bg,
      "--skeleton-highlight": darkTheme.skeleton.highlight,
      // Soft-editorial shadows (dark)
      "--shadow-card": shadows.dark.card,
      "--shadow-card-hover": shadows.dark.cardHover,
      "--shadow-popover": shadows.dark.popover,
      // Accents (dark)
      ...flattenAccents(accentsDark),
    },
    // Global table styles
    table: {
      width: "100%",
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    thead: {
      backgroundColor: "var(--table-header-bg) !important",
      borderBottomWidth: "0px",
    },
    "thead th": {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 500,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "var(--table-header-text)",
      backgroundColor: "var(--table-header-bg) !important",
    },
    "tbody tr": {
      backgroundColor: "var(--table-row-bg) !important",
      borderBottomWidth: "0px",
      transition: "background-color 0.2s",
    },
    "tbody tr:hover": {
      backgroundColor: "var(--table-row-hover) !important",
    },
    "tbody td": {
      padding: "16px",
      fontSize: "14px",
      color: "var(--text-primary)",
      borderBottomWidth: "0px",
      backgroundColor: "transparent !important",
    },
    // Force remove borders from Chakra table components
    "table thead": {
      borderBottom: "none !important",
    },
    "table thead tr": {
      borderBottom: "none !important",
    },
    "table tbody tr": {
      borderBottom: "none !important",
      backgroundColor: "var(--table-row-bg) !important",
    },
    "table tbody tr:hover": {
      backgroundColor: "var(--table-row-hover) !important",
    },
    "table td, table th": {
      borderBottom: "none !important",
    },
    // Override Chakra Card background colors
    "[data-scope='card'][data-part='root']": {
      backgroundColor: "var(--chakra-colors-bg-panel) !important",
    },
    // Textarea styles - using Chakra data attributes
    "textarea, [data-scope='textarea']": {
      backgroundColor: "var(--textarea-bg) !important",
      borderColor: "var(--textarea-border) !important",
      color: "var(--textarea-text) !important",
      borderWidth: "1px",
      borderStyle: "solid",
    },
    "textarea:focus, textarea:focus-visible, [data-scope='textarea']:focus, [data-scope='textarea']:focus-visible":
      {
        borderColor: "var(--textarea-border-focus) !important",
        boxShadow: "0 0 0 1px var(--textarea-border-focus) !important",
        outline: "none",
      },
    "textarea::placeholder, [data-scope='textarea']::placeholder": {
      color: "var(--textarea-placeholder) !important",
    },
    // Input styles - same as textarea
    "input[type='text'], input[type='email'], input[type='password'], input[type='number'], input[type='search'], input[type='tel'], input[type='url'], [data-scope='input']":
      {
        backgroundColor: "#F6F6FC !important",
        borderColor: "#E3E3E7 !important",
        color: "#141417 !important",
        borderWidth: "1px",
        borderStyle: "solid",
      },
    "html.dark input[type='text'], html.dark input[type='email'], html.dark input[type='password'], html.dark input[type='number'], html.dark input[type='search'], html.dark input[type='tel'], html.dark input[type='url'], html.dark [data-scope='input']":
      {
        backgroundColor: "#3B3B3C !important",
        borderColor: "#515153 !important",
        color: "#FFFFFF !important",
      },
    "input[type='text']:focus, input[type='email']:focus, input[type='password']:focus, input[type='number']:focus, input[type='search']:focus, input[type='tel']:focus, input[type='url']:focus, [data-scope='input']:focus, input:focus-visible, [data-scope='input']:focus-visible":
      {
        borderColor: "#515153 !important",
        boxShadow: "0 0 0 1px #515153 !important",
        outline: "none",
      },
    "html.dark input[type='text']:focus, html.dark input[type='email']:focus, html.dark input[type='password']:focus, html.dark input[type='number']:focus, html.dark input[type='search']:focus, html.dark input[type='tel']:focus, html.dark input[type='url']:focus, html.dark [data-scope='input']:focus, html.dark input:focus-visible, html.dark [data-scope='input']:focus-visible":
      {
        borderColor: "#88898E !important",
        boxShadow: "0 0 0 1px #88898E !important",
      },
    // Global focus styles - remove black borders
    "*:focus, *:focus-visible, button:focus, button:focus-visible, a:focus, a:focus-visible, select:focus, select:focus-visible":
      {
        outline: "none !important",
        boxShadow: "none !important",
      },
    // Chakra-specific focus overrides
    "[data-focus], [data-focus-visible]": {
      outline: "none !important",
      boxShadow: "none !important",
    },
    "input::placeholder, [data-scope='input']::placeholder": {
      color: "#646469 !important",
    },
    "html.dark input::placeholder, html.dark [data-scope='input']::placeholder":
      {
        color: "#88898E !important",
      },
    // Code block styling (for Box components with monospace text)
    ".code-block": {
      backgroundColor: "var(--code-block-bg) !important",
      color: "var(--code-block-text) !important",
      borderColor: "var(--code-block-border)",
    },
    // (Outline button border softening is handled at the Button wrapper level
    // in components/ui/button.tsx — Chakra v3 doesn't expose a data-variant
    // attribute we could target here.)
    //
    // Slim, low-contrast scrollbar utility — apply via className="thin-scrollbar"
    // to any element with overflow:auto that should fade visually (sidebar, side panels).
    ".thin-scrollbar": {
      scrollbarWidth: "thin",
      scrollbarColor: "var(--border-subtle) transparent",
    },
    ".thin-scrollbar::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    ".thin-scrollbar::-webkit-scrollbar-track": {
      background: "transparent",
    },
    ".thin-scrollbar::-webkit-scrollbar-thumb": {
      background: "var(--border-subtle)",
      borderRadius: "3px",
    },
    ".thin-scrollbar::-webkit-scrollbar-thumb:hover": {
      background: "var(--border-default)",
    },
    ".thin-scrollbar::-webkit-scrollbar-corner": {
      background: "transparent",
    },
    // Global body typography
    "html, body": {
      fontFamily: "var(--font-body)",
      fontSize: typography.body.fontSize,
      lineHeight: typography.body.lineHeight,
    },
    // Default heading typography + text color
    "h1, h2, h3, h4, h5, h6": {
      fontFamily: "var(--font-heading)",
      fontWeight: String(typography.heading.fontWeight),
      lineHeight: typography.heading.lineHeight,
      letterSpacing: typography.heading.letterSpacing,
      color: "var(--text-primary)",
    },
    // Raise Chakra dialog/modal above the embedded widget iframe (z-index: 999999)
    // and account for the widget sidebar width when centering modals
    "[data-scope='dialog'][data-part='backdrop']": {
      zIndex: "1000001 !important",
    },
    "[data-scope='dialog'][data-part='positioner']": {
      zIndex: "1000002 !important",
      right: "var(--widget-sidebar-width, 0px) !important",
    },
  },
});

export const system = createSystem(config);
